import { useState, useRef, useEffect, useCallback } from "react";
import { MessageCircle, X, Send, User, Bot } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { supabase } from "@/integrations/supabase/client";

type Msg = { role: "user" | "assistant"; content: string };

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat-support`;

function isBusinessHours(): boolean {
  const now = new Date();
  const pst = new Date(
    now.toLocaleString("en-US", { timeZone: "America/Los_Angeles" })
  );
  const day = pst.getDay();
  const hour = pst.getHours();
  return day >= 1 && day <= 5 && hour >= 10 && hour < 16;
}

const HANDOFF_TRIGGERS = [
  "speak with live agent",
  "speak to a live agent",
  "talk to a person",
  "talk to a human",
  "speak with a human",
  "speak to someone",
  "live agent",
  "real person",
  "human agent",
];

function detectHandoffIntent(text: string): boolean {
  const lower = text.toLowerCase().trim();
  return HANDOFF_TRIGGERS.some((t) => lower.includes(t));
}

async function streamChat({
  messages,
  onDelta,
  onDone,
}: {
  messages: Msg[];
  onDelta: (delta: string) => void;
  onDone: () => void;
}) {
  const resp = await fetch(CHAT_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
    },
    body: JSON.stringify({ messages }),
  });

  if (!resp.ok || !resp.body) {
    const errText = await resp.text().catch(() => "");
    throw new Error(errText || `Error ${resp.status}`);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let streamDone = false;

  while (!streamDone) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });

    let idx: number;
    while ((idx = buf.indexOf("\n")) !== -1) {
      let line = buf.slice(0, idx);
      buf = buf.slice(idx + 1);
      if (line.endsWith("\r")) line = line.slice(0, -1);
      if (line.startsWith(":") || line.trim() === "") continue;
      if (!line.startsWith("data: ")) continue;
      const json = line.slice(6).trim();
      if (json === "[DONE]") {
        streamDone = true;
        break;
      }
      try {
        const parsed = JSON.parse(json);
        const content = parsed.choices?.[0]?.delta?.content as string | undefined;
        if (content) onDelta(content);
      } catch {
        buf = line + "\n" + buf;
        break;
      }
    }
  }

  // flush
  if (buf.trim()) {
    for (let raw of buf.split("\n")) {
      if (!raw) continue;
      if (raw.endsWith("\r")) raw = raw.slice(0, -1);
      if (!raw.startsWith("data: ")) continue;
      const json = raw.slice(6).trim();
      if (json === "[DONE]") continue;
      try {
        const parsed = JSON.parse(json);
        const content = parsed.choices?.[0]?.delta?.content as string | undefined;
        if (content) onDelta(content);
      } catch {}
    }
  }

  onDone();
}

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [handoff, setHandoff] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [contactSending, setContactSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const online = isBusinessHours();

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, handoff, submitted, scrollToBottom]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const triggerHandoff = useCallback(() => {
    setHandoff(true);
  }, []);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");

    // Check for direct handoff intent from user
    if (detectHandoffIntent(text)) {
      const userMsg: Msg = { role: "user", content: text };
      setMessages((prev) => [...prev, userMsg]);
      const agentMsg: Msg = {
        role: "assistant",
        content:
          "Let me connect you with our team! Please share your contact details below and we'll get back to you shortly.",
      };
      setMessages((prev) => [...prev, agentMsg]);
      triggerHandoff();
      return;
    }

    const userMsg: Msg = { role: "user", content: text };
    setMessages((prev) => [...prev, userMsg]);
    setLoading(true);

    let assistantSoFar = "";
    const upsert = (chunk: string) => {
      assistantSoFar += chunk;

      // Check for handoff marker mid-stream
      if (assistantSoFar.includes("[HANDOFF_REQUESTED]")) {
        const cleaned = assistantSoFar.replace("[HANDOFF_REQUESTED]", "").trim();
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.role === "assistant") {
            return prev.map((m, i) =>
              i === prev.length - 1 ? { ...m, content: cleaned } : m
            );
          }
          return cleaned
            ? [...prev, { role: "assistant", content: cleaned }]
            : prev;
        });
        triggerHandoff();
        return;
      }

      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === "assistant") {
          return prev.map((m, i) =>
            i === prev.length - 1 ? { ...m, content: assistantSoFar } : m
          );
        }
        return [...prev, { role: "assistant", content: assistantSoFar }];
      });
    };

    try {
      await streamChat({
        messages: [...messages, userMsg],
        onDelta: upsert,
        onDone: () => setLoading(false),
      });

      // Final check for handoff in completed response
      if (
        assistantSoFar.includes("[HANDOFF_REQUESTED]") &&
        !handoff
      ) {
        triggerHandoff();
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Sorry, something went wrong. Please try again.",
        },
      ]);
      setLoading(false);
    }
  };

  const validateContact = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const digits = contact.phone.replace(/[^\d]/g, "");
    const errors: string[] = [];
    if (!contact.name.trim()) errors.push("Name is required");
    if (!emailRegex.test(contact.email)) errors.push("Valid email is required");
    if (digits.length !== 10) errors.push("Valid 10-digit U.S. phone is required");
    return errors;
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateContact();
    if (errors.length > 0) return;
    setContactSending(true);
    try {
      const id = crypto.randomUUID();
      await supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "chat-handoff-notification",
          recipientEmail: "sales@jayz.one",
          idempotencyKey: `chat-handoff-${id}`,
          templateData: {
            name: contact.name,
            email: contact.email,
            phone: contact.phone,
            transcript: messages,
          },
        },
      });
    } catch {
      // Still show success
    }
    setContactSending(false);
    setSubmitted(true);
  };

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Open support chat"
          className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform duration-200 hover:scale-105 active:scale-95"
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 flex h-[520px] w-[370px] flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl sm:w-[400px]">
          {/* Header */}
          <div className="flex items-center justify-between bg-primary px-5 py-3.5">
            <div className="flex items-center gap-3">
              <Bot className="h-5 w-5 text-primary-foreground/80" />
              <div>
                <p className="text-sm font-semibold text-primary-foreground">
                  JAYZ Support
                </p>
                <div className="flex items-center gap-1.5">
                  <span
                    className={`inline-block h-2 w-2 rounded-full ${
                      online ? "bg-green-400" : "bg-white/40"
                    }`}
                  />
                  <span className="text-[11px] text-primary-foreground/70">
                    {online ? "Live agents online" : "Live agents offline"}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close chat"
              className="rounded-lg p-1 text-primary-foreground/70 transition-colors hover:bg-white/10 hover:text-primary-foreground active:scale-95"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
          >
            {/* Welcome */}
            {messages.length === 0 && !handoff && (
              <div className="flex gap-2.5">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Bot className="h-3.5 w-3.5" />
                </div>
                <div className="rounded-xl rounded-tl-sm bg-muted px-3.5 py-2.5 text-[13px] leading-relaxed text-foreground">
                  Hi there! 👋 I'm the JAYZ Services assistant. Ask me about
                  our IT support, consulting, software development, or
                  automation services. Or type{" "}
                  <strong>"speak with live agent"</strong> to connect with our
                  team.
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex gap-2.5 ${
                  msg.role === "user" ? "flex-row-reverse" : ""
                }`}
              >
                <div
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
                    msg.role === "user"
                      ? "bg-secondary/10 text-secondary"
                      : "bg-primary/10 text-primary"
                  }`}
                >
                  {msg.role === "user" ? (
                    <User className="h-3.5 w-3.5" />
                  ) : (
                    <Bot className="h-3.5 w-3.5" />
                  )}
                </div>
                <div
                  className={`max-w-[75%] rounded-xl px-3.5 py-2.5 text-[13px] leading-relaxed ${
                    msg.role === "user"
                      ? "rounded-tr-sm bg-primary text-primary-foreground"
                      : "rounded-tl-sm bg-muted text-foreground"
                  }`}
                >
                  {msg.role === "assistant" ? (
                    <div className="prose prose-sm max-w-none [&_p]:m-0 [&_p]:leading-relaxed [&_ul]:my-1 [&_ol]:my-1 [&_li]:my-0 [&_strong]:text-foreground">
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                  ) : (
                    msg.content
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex gap-2.5">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Bot className="h-3.5 w-3.5" />
                </div>
                <div className="rounded-xl rounded-tl-sm bg-muted px-3.5 py-2.5">
                  <div className="flex gap-1">
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/50 [animation-delay:0ms]" />
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/50 [animation-delay:150ms]" />
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/50 [animation-delay:300ms]" />
                  </div>
                </div>
              </div>
            )}

            {/* Contact form for handoff */}
            {handoff && !submitted && (
              <div className="mt-2 rounded-xl border border-border bg-card p-4">
                <p className="mb-3 text-[13px] font-medium text-foreground">
                  Share your details and we'll reach out shortly:
                </p>
                <form onSubmit={handleContactSubmit} className="space-y-2.5">
                  <input
                    required
                    placeholder="Name"
                    value={contact.name}
                    onChange={(e) =>
                      setContact({ ...contact, name: e.target.value })
                    }
                    className="flex h-9 w-full rounded-lg border border-input bg-background px-3 text-[13px] text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                  <div>
                    <input
                      required
                      type="email"
                      placeholder="Email"
                      value={contact.email}
                      onChange={(e) =>
                        setContact({ ...contact, email: e.target.value })
                      }
                      className="flex h-9 w-full rounded-lg border border-input bg-background px-3 text-[13px] text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                    {contact.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email) && (
                      <p className="mt-0.5 text-[11px] text-destructive">Please enter a valid email address</p>
                    )}
                  </div>
                  <div>
                    <input
                      required
                      type="tel"
                      placeholder="Phone (e.g. 555-123-4567)"
                      value={contact.phone}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^\d]/g, "").slice(0, 10);
                        let formatted = raw;
                        if (raw.length > 6) formatted = `${raw.slice(0,3)}-${raw.slice(3,6)}-${raw.slice(6)}`;
                        else if (raw.length > 3) formatted = `${raw.slice(0,3)}-${raw.slice(3)}`;
                        setContact({ ...contact, phone: formatted });
                      }}
                      className="flex h-9 w-full rounded-lg border border-input bg-background px-3 text-[13px] text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                    {contact.phone && contact.phone.replace(/[^\d]/g, "").length !== 10 && (
                      <p className="mt-0.5 text-[11px] text-destructive">Please enter a valid 10-digit U.S. phone number</p>
                    )}
                  </div>
                  <button
                    type="submit"
                    disabled={contactSending}
                    className="flex h-9 w-full items-center justify-center rounded-lg bg-primary text-[13px] font-semibold text-primary-foreground transition-colors hover:bg-primary/85 active:scale-[0.97] disabled:opacity-60"
                  >
                    {contactSending ? "Sending…" : "Submit"}
                  </button>
                </form>
              </div>
            )}

            {submitted && (
              <div className="mt-2 rounded-xl border border-green-200 bg-green-50 p-4 text-center">
                <p className="text-[13px] font-medium text-green-800">
                  Thank you! Our agent will contact you shortly. 🙌
                </p>
              </div>
            )}
          </div>

          {/* Input */}
          {!handoff && (
            <div className="border-t border-border px-3 py-3">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  send();
                }}
                className="flex gap-2"
              >
                <input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Type a message…"
                  disabled={loading}
                  className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-[13px] text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
                />
                <button
                  type="submit"
                  disabled={loading || !input.trim()}
                  aria-label="Send message"
                  className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground transition-colors hover:bg-primary/85 active:scale-95 disabled:opacity-40"
                >
                  <Send className="h-4 w-4" />
                </button>
              </form>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ChatWidget;
